import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { linabase } from '@/lib/linabase';
import { trackEvent } from '@/lib/umami';
import { User, Profile } from '@/types';

export function useProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data } = await api.get<{ user: User; profile: Profile }>('/api/auth/me');
      return data;
    },
    enabled: options?.enabled,
  });
}

/**
 * Check if the user's username looks auto-generated (needs setup).
 * Returns true if the profile username matches the email prefix pattern
 * that the handle_new_user trigger generates for OAuth users.
 */
export function useNeedsUsernameSetup(): boolean {
  const { data } = useProfile();
  if (!data?.profile || !data?.user) return false;

  const username = data.profile.username;
  const email = data.user.email || '';
  const emailPrefix = email.split('@')[0]?.toLowerCase().replace(/[^a-z0-9-]/g, '') || '';

  if (!emailPrefix) return false;

  // Exact match with email prefix
  if (username === emailPrefix) return true;

  // Matches trigger's random suffix pattern: "prefix-xxxx" or "prefix-xxxxxxxx"
  const suffixPattern = new RegExp(`^${emailPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-[a-f0-9]{4,8}$`);
  return suffixPattern.test(username);
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: { full_name: string }) => {
      const { data } = await api.put('/api/profile', updates);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: async () => {
      await api.delete('/api/profile');
    },
    onSuccess: async () => {
      trackEvent('account-deleted');
      await linabase.auth.signOut();
      window.location.href = '/';
    },
  });
}

export function useExportData() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get('/api/profile/export');
      return data;
    },
    onSuccess: (data) => {
      trackEvent('data-exported');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split('T')[0];
      const a = document.createElement('a');
      a.href = url;
      a.download = `hooklink-export-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
